import { vec3 } from 'gl-matrix';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import type { Environment } from '~/engine/data/Environment';
import { getPreviewColor } from '~/components/toolbar/OperationsToolbar/margin/marginToolbarUtils';

export interface PreviewOverlayPort {
    show(handle: ContourTexture): void;
    hide(): void;
}

/**
 * Render-only overlay for a BORROWED contour texture — an agent proposal's
 * already-computed geometry, shown translucently while its card is hovered.
 * Publishes a wrapper to the engine's single preview slot (the same channel the
 * manual toolbar uses) and clears it on hide.
 *
 * INVARIANT: never delete the borrowed texture. The proposal's apply() commits
 * the SAME handle (replaceTexture adopts it without cloning), so deleting here
 * would corrupt a later Apply. show/hide therefore only set/clear the slot —
 * and the slot is cleared only while it still holds OUR wrapper, so a hide can
 * never stomp the toolbar's own (owned) preview.
 */
export function createPreviewOverlayPort(
    env: Environment | null
): PreviewOverlayPort {
    let ours: CustomTargetObject | undefined;
    return {
        show(handle: ContourTexture): void {
            if (!env?.image) {
                return;
            }
            // Single-owner slot: only take it when it is free or already ours.
            // If another source holds it (e.g. the manual toolbar's own
            // margin/ring preview, which shares this slot), this hover-preview
            // yields rather than stomping it.
            if (
                env.operationPreviewStructure !== undefined &&
                env.operationPreviewStructure !== ours
            ) {
                return;
            }
            const overlay = new CustomTargetObject(
                env.image,
                handle,
                getPreviewColor(vec3.create())
            );
            overlay.renderOptions = { fillAlpha: 0.18 };
            ours = overlay;
            env.operationPreviewStructure = overlay;
            env.triggerContourLayerUpdate();
        },
        hide(): void {
            // `ours` must be truthy: a falsy `ours` would match the slot's
            // `undefined` default and fire a needless cross-view update even
            // when this port never showed anything.
            if (ours && env && env.operationPreviewStructure === ours) {
                env.operationPreviewStructure = undefined;
                env.triggerContourLayerUpdate();
            }
            ours = undefined;
        },
    };
}
